import { memo, useCallback, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import dotize from 'dotize';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import * as yup from 'yup';

import FileUpload from '@/components/atoms/FileUpload/FileUpload';
import FormDialog from '@/components/atoms/FormDialog/FormDialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useCloseModal } from '@/hooks/useCloseModal';
import { useDeskId } from '@/hooks/useDeskId';
import { selectAccountSlug } from '@/redux/session/selectors';
import { UploadedFile } from '@/shared/types/server';
import { createBundleSchema } from '@/util/validation/schema-builders';

import { setDraftBundle } from '../redux/actions';

function ModalBundleCreation() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [importedBundle, setImportedBundle] = useState({});
  const deskId = useDeskId();
  const slug = useSelector(selectAccountSlug);
  const dispatch = useDispatch();
  const closeModal = useCloseModal();

  const bundleSchema = createBundleSchema(deskId);
  type FormValues = yup.InferType<typeof bundleSchema>;

  const form = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { name: '', description: '' },
    resolver: yupResolver<FormValues, unknown, FormValues>(bundleSchema),
  });
  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = form;

  const onSubmit = useCallback(
    (bundle: FormValues) => {
      dispatch(
        setDraftBundle({
          ...bundle,
          context: importedBundle,
          dirty: Object.keys(importedBundle).length > 0,
        })
      );
      navigate(`/${slug}/environments/${deskId}/context_bundles/draft`);
      closeModal();
    },
    [closeModal, deskId, dispatch, importedBundle, navigate, slug]
  );

  const onJsonFileUpload = useCallback(async ({ data }: UploadedFile) => {
    setImportedBundle(dotize.convert(data));
  }, []);

  return (
    <FormDialog
      title={t('bundles.create_a_bundle')}
      submitLabel={t('common.create')}
      onSubmit={handleSubmit(onSubmit)}
      submitDisabled={!isDirty || !isValid}
      submitOnEnter
    >
      <Form {...form}>
        <form autoComplete="off" className="flex flex-col gap-4">
          <FormField
            control={control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('common.name')}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t('common.name_placeholder')} autoFocus />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('common.description')}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t('common.description_placeholder')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {/* Fixed height: FileUpload is `height: 100%`, which would otherwise resolve against
              the whole form and overflow the dialog. */}
          <div className="h-32">
            <FileUpload
              message={t('bundles.drop_exported_brain')}
              onFileUploaded={onJsonFileUpload}
              parseJson
              accept={{ 'application/json': ['.json'] }}
              onFileRemoved={() => setImportedBundle({})}
            />
          </div>
        </form>
      </Form>
    </FormDialog>
  );
}

export default memo(ModalBundleCreation);
